import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { EffortsService } from '../../services/efforts.service';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { Effort } from '../../models/effort';

@Component({
  selector: 'app-effort-chooser',
  templateUrl: './effort-chooser.component.html',
  styleUrls: ['./effort-chooser.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatOptionModule,
    MatSelectModule,
    MatListModule,
    DragDropModule,
    CommonModule
  ]
})
export class EffortChooserComponent implements OnInit, OnDestroy {
  efforts: Effort[] = [];
  filteredEfforts: Effort[] = [];
  loaded = false;

  form: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private effortsService: EffortsService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EffortChooserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { canSelect?: (effort: Effort) => boolean } | null
  ) {
    this.form = this.fb.group({
      searchTerm: [''],
      selectedEffort: [null, [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.effortsService.filterEfforts([], 10000)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.efforts = response.efforts.filter(effort => this.data?.canSelect?.(effort) ?? true);
          this.loaded = true;
          this.applyFilter(this.form.get('searchTerm')!.value || '');
          if (!this.efforts.includes(this.form.get('selectedEffort')!.value)) {
            this.form.get('selectedEffort')!.setValue(null);
          }
        },
        error: (err) => console.error('Error fetching efforts:', err)
      });

    this.form.get('searchTerm')!
      .valueChanges
      .pipe(
        startWith(this.form.get('searchTerm')!.value),
        debounceTime(150),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((term: string) => this.applyFilter(term || ''));
  }

  private applyFilter(term: string): void {
    const terms = term.toLowerCase().split(' ').filter(t => t !== '');
    this.filteredEfforts = this.efforts.filter(effort =>
      terms.every(t =>
        (effort.title?.toLowerCase().includes(t)) ||
        (effort.key?.toLowerCase().includes(t))
      )
    );
  }

  onSelectEffort(effort: Effort): void {
    if (!this.efforts.includes(effort)) return;
    this.form.get('selectedEffort')!.setValue(effort);
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  onSelectClick(): void {
    const selected = this.form.get('selectedEffort')!.value;
    if (selected && this.efforts.includes(selected) && (this.data?.canSelect?.(selected) ?? true)) {
      this.dialogRef.close(selected);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
